import { useId, useState, type FormEvent } from "react";
import { useCreateSecret } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface CreateSecretModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

export function CreateSecretModal({ open, fleetId, onClose }: CreateSecretModalProps) {
  const nameId = useId();
  const valueId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateSecret(fleetId);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");

  function handleClose() {
    setName("");
    setValue("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    mutation.mutate(
      { name: submittedName, value },
      {
        onSuccess: (created) => {
          pushToast({ title: `Secret "${created.name}" created.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't create "${submittedName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // The value has been handed off to the mutation above; there's no reason
    // to keep it in component state a moment longer, success or not.
    setValue("");
  }

  return (
    <Modal open={open} title="New secret" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={253}
          />
        </label>

        <label className="form-field" htmlFor={valueId}>
          <span>Value</span>
          <input
            id={valueId}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
          />
          <span className="form-field__hint">
            Sealed on write. Never shown again — rotate later if you need to replace it.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !name.trim() || !value}>
            {mutation.isPending ? "Creating…" : "Create secret"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
